/**
 * Trunnion AI - Search Page
 * /search - Full search results with entity type tabs.
 */

import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { SearchResultCard } from '@/components/search/SearchResultCard';
import { useGlobalSearch } from '@/hooks/queries/useSearch';
import type { SearchEntityType, SearchResult } from '@/types';

const ENTITY_TABS = ['all', 'workflows', 'agents', 'executions', 'audit'] as const;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';
  const [query, setQuery] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState<string>('all');

  const { data: results, isLoading } = useGlobalSearch(query, activeTab === 'all' ? undefined : activeTab as SearchEntityType);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Search</h1>
        <p className="text-sm text-trunnion-slate-400 mt-1">
          Search across workflows, agents, executions, and audit logs.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-trunnion-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSearchParams(e.target.value ? { q: e.target.value } : {});
          }}
          placeholder="Search workflows, agents, executions..."
          className="w-full rounded-xl border border-trunnion-slate-700 bg-trunnion-slate-800 py-3 pl-11 pr-4 text-white placeholder-trunnion-slate-400 focus:border-trunnion-gold-500 focus:outline-none focus:ring-1 focus:ring-trunnion-gold-500"
          autoFocus
        />
      </div>

      {/* Entity type tabs */}
      <div className="flex items-center gap-1 border-b border-trunnion-slate-700">
        {ENTITY_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-trunnion-gold-500 text-trunnion-gold-500'
                : 'text-trunnion-slate-400 hover:text-white'
            }`}
          >
            {tab}
            {results && tab !== 'all' && (
              <span className="ml-1.5 text-xs text-trunnion-slate-500">
                ({results.filter((r: SearchResult) => r.type === tab.slice(0, -1)).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Results */}
      <div className="space-y-3">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-trunnion-gold-500 border-t-transparent" />
          </div>
        )}
        {!isLoading && results && results.length > 0 && (
          results.map((result: SearchResult) => (
            <SearchResultCard key={`${result.type}-${result.id}`} result={result} />
          ))
        )}
        {!isLoading && query && results?.length === 0 && (
          <Card>
            <div className="flex flex-col items-center py-12 text-center">
              <Search className="h-12 w-12 text-trunnion-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-white">No results found</h3>
              <p className="text-sm text-trunnion-slate-400 mt-1">
                No matches for &quot;{query}&quot;. Try different search terms.
              </p>
            </div>
          </Card>
        )}
        {!query && (
          <Card>
            <div className="flex flex-col items-center py-12 text-center">
              <Search className="h-12 w-12 text-trunnion-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-white">Start searching</h3>
              <p className="text-sm text-trunnion-slate-400 mt-1">
                Type a query above to search across all entities.
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
